"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useDebouncedValue } from "@/hooks/use-debounced-value";

import { mergeRemoteFacetItems, type FacetItem } from "./merge-remote-facet-items";
import type { SearchFacetInput } from "./search-facet-query";
import { getSearchFacetQueryOptions } from "./search-facet-query";

interface UseSearchFacetOptionsInput {
  facet: SearchFacetInput["facet"];
  filter: SearchFacetInput["filter"];
  initialItems: FacetItem[];
  query: string;
  selectedValues?: string[];
}

export function useSearchFacetOptions(input: UseSearchFacetOptionsInput) {
  const debouncedQuery = useDebouncedValue(input.query, 250);
  const hasQuery = input.query.trim().length > 0;
  const queryIsDebounced = input.query === debouncedQuery;
  const facetQuery = useQuery({
    ...getSearchFacetQueryOptions({
      facet: input.facet,
      filter: input.filter,
      query: debouncedQuery,
    }),
    enabled: debouncedQuery.trim().length > 0,
  });
  const remoteSearchActive = hasQuery && queryIsDebounced && facetQuery.data !== undefined;
  const items = useMemo(() => {
    if (!remoteSearchActive) return input.initialItems;
    return mergeRemoteFacetItems({
      initialItems: input.initialItems,
      remoteItems: facetQuery.data ?? [],
      selectedValues: input.selectedValues,
    });
  }, [facetQuery.data, input.initialItems, input.selectedValues, remoteSearchActive]);

  return { items, remoteSearchActive };
}
